import { Component, computed, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ZiroIcons } from '../../models/ziro-icons.enum';
import { NgbPopover, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { LoadingBlockModule } from '../../loading-block/loading-block.module';
import { LANGUAGE_DISPLAY_BY_CODE } from '../../language-codes/language-codes.constants';
import { PipesModule } from '../../pipes/pipes.module';

export interface ExplorerLinkClickedEvent {
  subchartId: string;
  direction?: 'previous' | 'next';
}

@Component({
  selector: 'ziro-explorer-node',
  imports: [CommonModule, TranslateModule, NgbTooltip, LoadingBlockModule, NgbPopover, PipesModule],
  templateUrl: 'explorer-node.component.html',
  styleUrls: ['explorer-node.component.scss'],
})
export class ExplorerNodeComponent {
  readonly ZiroIcons = ZiroIcons;

  readonly title = input.required<string>();
  readonly label = input.required<string>();
  readonly sublabel = input<string>();
  readonly warnings = input<string[]>();
  readonly icon = input<ZiroIcons>();
  readonly disabled = input<boolean>(false);
  readonly previousLink = input<string>();
  readonly nextLink = input<string>();
  readonly previouslyViewedSubchartId = input<string>();
  readonly previouslyViewedTooltip = input<string>();

  readonly audioFileUrls = input<string[]>([]);
  readonly mediaDisabled = input<boolean>(false);
  readonly activeGreetingAudioUrl = input<string | null>(null);
  readonly activeGreetingAudioLoadingUrl = input<string | null>(null);
  readonly activeGreetingAudioDownloadUrl = input<string | null>(null);

  readonly linkClicked = output<ExplorerLinkClickedEvent>();
  readonly playGreetingAudio = output<string>();
  readonly stopGreetingAudio = output<void>();
  readonly downloadGreetingAudio = output<string>();

  readonly hasMultipleAudioFiles = computed<boolean>(() => this.audioFileUrls()?.length > 1);

  private readonly languageDisplayByCode = LANGUAGE_DISPLAY_BY_CODE;

  readonly isPreviouslyViewedNode = computed(() => {
    return (
      this.previouslyViewedSubchartId() &&
      (this.previouslyViewedSubchartId() === this.previousLink() ||
        this.previouslyViewedSubchartId() === this.nextLink())
    );
  });

  protected readonly ziroIcons = ZiroIcons;

  stopPlayingCurrentAudioGreeting(): void {
    this.stopGreetingAudio.emit();
  }

  onSingleAudioNodePlayClick(event: MouseEvent): void {
    event.stopPropagation();

    if (this.isCurrentAudioLoading()) {
      return;
    }

    const path = this.audioFileUrls()?.[0];
    this.playGreetingAudio.emit(path);
  }

  onRowAudioPlayClick(event: MouseEvent, url: string): void {
    event.stopPropagation();

    if (this.isCurrentAudioLoading()) {
      return;
    }

    if (this.isCurrentAudioPlaying()) {
      this.stopPlayingCurrentAudioGreeting();
    }

    this.playGreetingAudio.emit(url);
  }

  onSingleAudioDownloadClick(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();

    if (this.isCurrentAudioDownloading()) {
      return;
    }

    const path = this.audioFileUrls()?.[0];
    this.downloadGreetingAudio.emit(path);
  }

  onRowAudioDownloadClick(event: MouseEvent, url: string): void {
    event.stopPropagation();
    event.preventDefault();

    if (this.isCurrentAudioDownloading()) {
      return;
    }

    this.downloadGreetingAudio.emit(url);
  }

  extractLanguageDisplay(url: string): string {
    const filename = url.split('/').filter(Boolean).pop() || '';
    const match = filename.match(/(\d{4,5})(?=\.[^.]+$)/);
    const code = match?.[1];
    return this.languageDisplayByCode[code];
  }

  isCurrentAudioLoading(): boolean {
    return this.audioFileUrls()?.includes(this.activeGreetingAudioLoadingUrl()) ?? false;
  }

  isCurrentAudioPlaying(): boolean {
    return this.audioFileUrls()?.includes(this.activeGreetingAudioUrl()) ?? false;
  }

  isCurrentAudioDownloading(): boolean {
    return this.audioFileUrls()?.includes(this.activeGreetingAudioDownloadUrl()) ?? false;
  }
}
